import moment from "moment";

import {
  DraftField,
  DraftSigner,
  FieldType,
  SignatureFieldView,
  SignaturePayload,
  SignatureRequestView,
} from "./signature.types";

export const SIGNER_COLORS = [
  "#F59E0B",
  "#10B981",
  "#0EA5E9",
  "#EC4899",
  "#8B5CF6",
  "#F97316",
];
export const MAX_SIGNERS = 6;

export const FIELD_LABELS: Record<FieldType, string> = {
  signature: "Signature",
  initials: "Initials",
  date: "Date signed",
  name: "Name",
  text: "Text box",
  checkbox: "Checkbox",
};

/** Default box size as a fraction of a US Letter page (612×792 pt). */
export const FIELD_SIZE: Record<FieldType, { width: number; height: number }> =
  {
    signature: { width: 0.3, height: 0.06 },
    initials: { width: 0.1, height: 0.05 },
    date: { width: 0.22, height: 0.03 },
    name: { width: 0.26, height: 0.03 },
    text: { width: 0.28, height: 0.03 },
    checkbox: { width: 0.03, height: 0.023 },
  };

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

/** A new field centred on the clicked point, kept inside the page. */
export const placeField = (
  type: FieldType,
  signerKey: string,
  page: number,
  cx: number,
  cy: number,
  key: string,
): DraftField => {
  const { width, height } = FIELD_SIZE[type];
  return {
    key,
    signerKey,
    type,
    page,
    width,
    height,
    x: clamp(cx - width / 2, 0, 1 - width),
    y: clamp(cy - height / 2, 0, 1 - height),
    required: type !== "checkbox",
    label:
      type === "text" ? "Text" : type === "checkbox" ? "Checkbox" : undefined,
  };
};

export const moveField = (f: DraftField, x: number, y: number): DraftField => ({
  ...f,
  x: clamp(x, 0, 1 - f.width),
  y: clamp(y, 0, 1 - f.height),
});

/** Why the request can't be sent yet, or null. */
export const draftProblem = (
  signers: DraftSigner[],
  fields: DraftField[],
): string | null => {
  if (!signers.length) return "Add at least one signer.";
  for (const s of signers) {
    if (
      s.kind === "client" &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email.trim())
    )
      return `Enter a valid email for ${s.name || "each signer"}.`;
    if (
      !fields.some(
        (f) =>
          f.signerKey === s.key &&
          (f.type === "signature" || f.type === "initials"),
      )
    )
      return `Place a signature field for ${
        s.kind === "firm" ? "yourself" : s.name || s.email
      }.`;
  }
  const emails = signers
    .map((s) => s.email.trim().toLowerCase())
    .filter(Boolean);
  if (new Set(emails).size !== emails.length)
    return "The same person is in the list twice.";
  return null;
};

export const buildSignaturePayload = (
  base: Omit<SignaturePayload, "signers" | "fields">,
  signers: DraftSigner[],
  fields: DraftField[],
): SignaturePayload => ({
  ...base,
  signers: signers.map((s) =>
    s.kind === "firm"
      ? { kind: "firm" }
      : {
          kind: "client",
          email: s.email.trim().toLowerCase(),
          name: s.name.trim() || undefined,
          role: s.role,
        },
  ),
  fields: fields.map((f) => ({
    signer: signers.findIndex((s) => s.key === f.signerKey),
    type: f.type,
    page: f.page,
    x: round(f.x),
    y: round(f.y),
    width: round(f.width),
    height: round(f.height),
    required: f.required,
    label: f.label,
  })),
});

const round = (n: number) => Math.round(n * 10000) / 10000;

/** "1 of 2 fields left for you". */
export const fieldsLeft = (
  mine: SignatureFieldView[],
  done: (f: SignatureFieldView) => boolean,
) => {
  const required = mine.filter((f) => f.required);
  return {
    left: required.filter((f) => !done(f)).length,
    total: required.length,
  };
};

/** The signer's own date for "Date signed" fields. */
export const localDateLabel = (d: Date = new Date()) =>
  moment(d).format("MMMM D, YYYY");

export type StatusTone = "waiting" | "done" | "stopped";
export const statusTone = (
  r: Pick<SignatureRequestView, "status">,
): StatusTone =>
  r.status === "sent"
    ? "waiting"
    : r.status === "completed"
    ? "done"
    : "stopped";

export const signerLine = (
  s: SignatureRequestView["signers"][number],
  inOrder: boolean,
  prev?: string,
) => {
  if (s.status === "signed")
    return `${s.name} · signed ${moment(s.signedAt).format("MMM D")}`;
  if (s.status === "declined")
    return `${s.name} · declined${
      s.declineReason ? `: ${s.declineReason}` : ""
    }`;
  if (s.status === "viewed") return `${s.name} · viewed, not signed`;
  if (s.status === "notified") return `${s.name} · not opened yet`;
  return `${s.name} · ${inOrder && prev ? `after ${prev}` : "waiting"}`;
};

export const initialsOf = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase())
    .slice(0, 3)
    .join("");

/** Files for "Request signature", newest first (files without a date last). */
export const newestFilesFirst = <T extends { createdDate?: string | null }>(
  files: T[],
): T[] => {
  const time = (f: T) => {
    const t = f.createdDate ? new Date(f.createdDate).getTime() : NaN;
    return Number.isNaN(t) ? -Infinity : t;
  };
  return [...files].sort((a, b) => time(b) - time(a));
};

/** Names as "A", "A and B", "A, B and C". */
const nameList = (names: string[]) =>
  names.length <= 1
    ? names[0] ?? ""
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/**
 * Text under "Thank you, you've signed" / "Everyone has signed" (Roadmap 1877).
 * Names who still has to sign; when nobody is left (only one signer, or the
 * signed copy is still being put together) it never says "the others".
 */
export const signedNoticeText = (
  view: Pick<
    SignatureRequestView,
    "status" | "documentName" | "firmName" | "signers"
  >,
) => {
  if (view.status === "completed")
    return `The signed copy of ${view.documentName}, with its certificate of completion, is saved with ${view.firmName}.`;
  const pending = view.signers
    .filter((s) => !s.isMe && s.status !== "signed" && s.status !== "declined")
    .map((s) => s.name);
  if (pending.length > 0)
    return `${view.documentName} is waiting for ${nameList(
      pending,
    )} to sign. We'll email everyone the signed copy when it's done.`;
  return `Your signature is saved. We're putting together the signed copy of ${view.documentName} and will email it to you when it's ready.`;
};

/** Only PDFs can be sent for e-signature. */
export const isPdfName = (name?: string | null) => /\.pdf$/i.test(name || "");

/**
 * The "Prepare & send" page for one of the client's files (owner 2026-10-28: a "Request
 * signature" icon on the profile Files card and in the Files tab's ⋯ menu). Only client
 * files (not older work-item files) can be sent: the server signs ContactDocuments only.
 */
export const signatureRequestPath = (
  customerId: string,
  documentId: string,
  name?: string | null,
) =>
  `/signatures/new?customer=${encodeURIComponent(
    customerId,
  )}&doc=${encodeURIComponent(documentId)}&name=${encodeURIComponent(
    name || "",
  )}`;
